import type { Column } from '@tanstack/react-table'
import type { CSSProperties } from 'react'
import { cn } from '#lib/utils'

export const DATA_TABLE_SURFACE_WIDTH =
	'max(100%, calc(var(--col-total-size) * 1px))'

export function getColumnWidthVar(columnId: string) {
	return `calc(var(--col-${columnId}-size) * 1px)`
}

/**
 * Pinned offsets read CSS vars from the scroll root so column resizing updates
 * sticky positions without re-rendering every pinned cell.
 */
export function getColumnPinStyles<TData>(
	column: Column<TData, unknown>,
	options?: { isHeader?: boolean }
): CSSProperties {
	const pinned = column.getIsPinned()
	if (pinned === false) {
		return {}
	}
	let boxShadow: string | undefined
	if (pinned === 'left' && column.getIsLastColumn('left')) {
		boxShadow = '-4px 0 4px -4px var(--border) inset'
	} else if (pinned === 'right' && column.getIsFirstColumn('right')) {
		boxShadow = '4px 0 4px -4px var(--border) inset'
	}
	return {
		position: 'sticky',
		left:
			pinned === 'left'
				? `var(--col-pin-left-${column.id}-offset)`
				: undefined,
		right:
			pinned === 'right'
				? `var(--col-pin-right-${column.id}-offset)`
				: undefined,
		zIndex: options?.isHeader === true ? 6 : 2,
		boxShadow
	}
}

/**
 * The background of a row under the pointer, open or selected: the primary tinted into the card,
 * opaque so a pinned cell hides what scrolls under it. `accent` is too close to white to see.
 */
export const ROW_STATE_BG =
	'group-hover:bg-[color-mix(in_oklab,var(--primary)_6%,var(--card))] group-has-aria-expanded:bg-[color-mix(in_oklab,var(--primary)_6%,var(--card))] group-data-[state=selected]:bg-[color-mix(in_oklab,var(--primary)_6%,var(--card))]'

export function getColumnBgClassName<TData>(
	column: Column<TData, unknown>
): string {
	const stateClasses = `shrink-0 grow-0 ${ROW_STATE_BG}`
	if (column.getIsPinned() === false) {
		return stateClasses
	}
	return cn('bg-card', stateClasses)
}
